import { Container } from "@mui/material";

import {
  ActionIcon,
  Badge,
  Button,
  CopyButton,
  Modal,
  Pagination,
  Popover,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";

import { GridRenderCellParams } from "@mui/x-data-grid";
import dayjs from "dayjs";
import DatagridDisplay from "../../Components/DatagridDisplay";

import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useContext, useState } from "react";
import MegaLoader from "../../Components/MegaLoader";
import { Colors, HttpSuccessCodes } from "../../Lib/Data";
import { Endpoints } from "../../Lib/Endpoints";
import {
  convertPhoneToWhatsapp,
  DefaultErrorNotification,
  DefaultSuccessNotification,
  generateRandomString,
  getCountry,
  getEllipsisFromString,
  getPaginationTotal,
} from "../../Lib/Methods";
import {
  DefaultAuthResponse,
  NonPaginatedResponse,
  PaginatedResponse,
} from "../../Lib/Responses";
import { PaginationProps, UserProfileProps } from "../../Lib/Types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import "./styles.scss";
import {
  IconBrandWhatsapp,
  IconKey,
  IconReload,
  IconSearch,
  IconUser,
  IconUserDown,
  IconUserUp,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { AppContext } from "../Dashboard";
const UsersPage: React.FC = () => {
  const context = useContext(AppContext);
  const searchForm = useForm({
    initialValues: {
      search: "",
    },
    validate: {
      search: (value) => (value && value.length > 0 ? null : true),
    },
  });
  const nameForm = useForm({
    initialValues: {
      name: "",
    },
    validate: {
      name: (value) => (value && value.length > 0 ? null : true),
    },
  });
  const [isSearching, setSearching] = useState<boolean>(false);

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });
  const {
    data: users,
    reloadData: reloadUsers,
    isLoading: isLoadingUsers,
  } = usePerformRequest<PaginatedResponse<UserProfileProps[]>>({
    method: "GET",
    route: Endpoints.GetUserProfiles(pagination.page, pagination.limit),
  });

  const [isUserModalOpened, { open: OpenUserModal, close: CloseUserModal }] =
    useDisclosure(false);

  const [currentUser, setCurrentUser] = useState<UserProfileProps>();

  const [isUserPromotionToggling, setUserPromotionToggling] =
    useState<boolean>(false);

  const GetPromotionIcon = currentUser?.isPromoter ? IconUserDown : IconUserUp;

  const TogglePromotion = async () => {
    if (currentUser) {
      const active = !currentUser.isPromoter;
      setUserPromotionToggling(true);
      const r = await PerformRequest({
        method: "PATCH",
        route: Endpoints.GetUpdatePromoterStatus(
          currentUser.promoterID as string
        ),
        data: {
          active: active,
        },
        callback: () => {
          setUserPromotionToggling(false);
        },
      });
      setUserPromotionToggling(false);
      if (r) {
        if (HttpSuccessCodes.includes(r.status)) {
          DefaultSuccessNotification(
            `Promotion set to ${active ? "active" : "inactive"}`
          );
          CloseUserModal();
          reloadUsers();
        } else {
          DefaultErrorNotification("An error occurred!");
        }
      } else {
        DefaultErrorNotification("An error occurred! Please try again");
      }
    }
  };
  const PromoteUser = async () => {
    if (currentUser) {
      setUserPromotionToggling(true);
      const r = await PerformRequest({
        method: "PATCH",
        route: Endpoints.GetConvertToPromoter(currentUser.id as string),

        callback: () => {
          setUserPromotionToggling(false);
        },
      });
      setUserPromotionToggling(false);
      if (r) {
        if (HttpSuccessCodes.includes(r.status)) {
          DefaultSuccessNotification(`User has been promoted!`);
          CloseUserModal();
          reloadUsers();
        } else {
          DefaultErrorNotification("An error occurred!");
        }
      } else {
        DefaultErrorNotification("An error occurred! Please try again");
      }
    }
  };

  const [isLoggingInAsUser, setLoggingInAsUser] = useState(false);
  const LoginAsUser = async () => {
    if (currentUser && currentUser.id) {
      setLoggingInAsUser(true);
      const r = await PerformRequest<NonPaginatedResponse<DefaultAuthResponse>>(
        {
          method: "GET",
          route: Endpoints.GetLoginAsUser(currentUser.id),
          callback: () => {
            setLoggingInAsUser(false);
          },
        }
      );
      setLoggingInAsUser(false);
      if (r) {
        if (HttpSuccessCodes.includes(r.status)) {
          DefaultSuccessNotification(`Logged in as ${currentUser.name}`);
          CloseUserModal();
          window.open(
            `${process.env.REACT_APP_USER_CLIENT_BASE_URL}/d/a/${r.data.data.token}`
          );
        } else {
          DefaultErrorNotification("An error occurred!");
        }
      } else {
        DefaultErrorNotification("An error occurred! Please try again");
      }
    }
  };

  const SearchForUser = async () => {
    const { hasErrors } = searchForm.validate();
    if (!hasErrors) {
      setSearching(true);
      const { search } = searchForm.values;
      const r = await PerformRequest<NonPaginatedResponse<UserProfileProps>>({
        method: "GET",
        route: Endpoints.GetSearchForUser(search),
        callback: () => {
          setSearching(false);
        },
      });
      setSearching(false);
      if (r) {
        if (r.status === 200) {
          DefaultSuccessNotification("User profile found!");
          setCurrentUser(r?.data?.data);
          OpenUserModal();
        } else if (r.status === 404) {
          DefaultErrorNotification("User does not exist!");
        } else {
          DefaultErrorNotification("An error occurred!");
        }
      } else {
        DefaultErrorNotification("An error occurred! Please try again");
      }
    }
  };

  const [isNameModalOpened, { open: OpenNameModal, close: CloseNameModal }] =
    useDisclosure(false);
  const [usersByName, setUsersByName] = useState<UserProfileProps[]>([]);

  const SearchForUserByName = async () => {
    const { hasErrors } = nameForm.validate();
    if (!hasErrors) {
      setSearching(true);
      const { name } = nameForm.values;
      const r = await PerformRequest<NonPaginatedResponse<UserProfileProps[]>>({
        method: "GET",
        route: Endpoints.GetSearchUsersByName(name),
        callback: () => {
          setSearching(false);
        },
      });
      setSearching(false);
      if (r) {
        if (r.status === 200) {
          DefaultSuccessNotification("User profile found!");
          setUsersByName(r?.data?.data);
          OpenNameModal();
        } else if (r.status === 404) {
          DefaultErrorNotification("User does not exist!");
        } else {
          DefaultErrorNotification("An error occurred!");
        }
      } else {
        DefaultErrorNotification("An error occurred! Please try again");
      }
    }
  };

  return (
    <Container>
      <div className="subscriptions-container">
        <div className="component-header">
          <div className="flex-row align-center justify-between width-100">
            <div className="flex-col">
              <Text className="label">Users</Text>
              <div className="flex-row align-end">
                <TextInput
                  w={270}
                  spellCheck={false}
                  {...searchForm.getInputProps("search")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      SearchForUser();
                    }
                  }}
                  placeholder="Enter Email, Phone or User ID"
                />
                <ActionIcon
                  onClick={SearchForUser}
                  size="lg"
                  ml={5}
                  h={36}
                  w={36}
                  variant="filled"
                  color={Colors.GreenPrimary}
                  radius="sm"
                  loading={isSearching}
                >
                  <IconSearch size={17} />
                </ActionIcon>
              </div>
              <br />
              <div className="flex-row align-end">
                <TextInput
                  w={270}
                  spellCheck={false}
                  {...nameForm.getInputProps("name")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      SearchForUserByName();
                    }
                  }}
                  placeholder="Enter Name"
                />
                <ActionIcon
                  onClick={SearchForUserByName}
                  size="lg"
                  ml={5}
                  h={36}
                  w={36}
                  variant="filled"
                  color={Colors.GreenPrimary}
                  radius="sm"
                  loading={isSearching}
                >
                  <IconSearch size={17} />
                </ActionIcon>
              </div>
            </div>
            <Button
              loading={isLoadingUsers}
              leftSection={<IconReload size={16} />}
              onClick={reloadUsers}
            >
              Reload
            </Button>
          </div>
          <br />
          {currentUser && (
            <Modal
              opened={isUserModalOpened}
              onClose={() => {
                CloseUserModal();
                setCurrentUser(undefined);
              }}
              title="Profile Details"
              centered
              size="xl"
            >
              <Table variant="vertical" layout="fixed" withTableBorder>
                <Table.Thead>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th w={200}>User ID</Table.Th>
                    <Table.Td>
                      <CopyButton value={currentUser.id}>
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? "Copied!" : "Copy to clipboard"}
                          >
                            <div
                              className="pointer flex-row align-center"
                              onClick={copy}
                              style={{
                                color: copied
                                  ? Colors.GreenPrimary
                                  : Colors.BluePrimary,
                                height: "100%",
                              }}
                            >
                              {currentUser.id}
                            </div>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>User</Table.Th>
                    <Table.Td>{currentUser?.name}</Table.Td>
                  </Table.Tr>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th w={200}>Email Address</Table.Th>
                    <Table.Td>
                      <CopyButton value={currentUser.email}>
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? "Copied!" : "Copy to clipboard"}
                          >
                            <div
                              className="pointer flex-row align-center"
                              onClick={copy}
                              style={{
                                color: copied
                                  ? Colors.GreenPrimary
                                  : Colors.BluePrimary,
                                height: "100%",
                              }}
                            >
                              {currentUser.email}
                            </div>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th w={200}>Phone</Table.Th>
                    <Table.Td>
                      <div className="flex-row align-center">
                        <CopyButton value={currentUser.phone}>
                          {({ copied, copy }) => (
                            <Tooltip
                              label={copied ? "Copied!" : "Copy to clipboard"}
                            >
                              <div
                                className="pointer flex-row align-center"
                                onClick={copy}
                                style={{
                                  color: copied
                                    ? Colors.GreenPrimary
                                    : Colors.BluePrimary,
                                  height: "100%",
                                }}
                              >
                                {currentUser.phone}
                              </div>
                            </Tooltip>
                          )}
                        </CopyButton>
                        <ActionIcon
                          variant="subtle"
                          size="md"
                          ml={5}
                          color={Colors.GreenPrimary}
                          onClick={() => {
                            window.open(
                              `https://wa.me/${convertPhoneToWhatsapp(
                                currentUser.phone
                              )}`,
                              "_blank"
                            );
                          }}
                        >
                          <IconBrandWhatsapp size={18} />
                        </ActionIcon>
                      </div>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Date Joined</Table.Th>
                    <Table.Td>
                      {dayjs(currentUser.createdAt).format(
                        "DD MMM YYYY - hh:mmA"
                      )}
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Gender</Table.Th>
                    <Table.Td>{currentUser.gender ?? "Not Specified"}</Table.Td>
                  </Table.Tr>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Country</Table.Th>
                    <Table.Td>
                      {getCountry(currentUser.country)?.flagEmoji}
                      &nbsp;
                      {getCountry(currentUser.country)?.country}
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Account Type</Table.Th>
                    <Table.Td
                      c={
                        currentUser.isPromoter
                          ? Colors.GreenSecondary
                          : Colors.BlackPrimary
                      }
                    >
                      {currentUser?.isPromoter ? "Promoter" : "Regular"}
                    </Table.Td>
                  </Table.Tr>
                </Table.Thead>
              </Table>
              <div className="flex-row align-center">
                <Popover
                  offset={20}
                  width={200}
                  position="bottom"
                  withArrow
                  shadow="md"
                >
                  <Popover.Target>
                    <Button
                      w={200}
                      color={
                        currentUser.isPromoter
                          ? Colors.OrangePrimary
                          : Colors.GreenPrimary
                      }
                      mt={10}
                      rightSection={<GetPromotionIcon size={17} />}
                    >
                      {currentUser.isPromoter
                        ? "Disable Promotion"
                        : "Promote User"}
                    </Button>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Text size="sm">
                      Are you sure you want to perform this action?
                    </Text>
                    <Button
                      onClick={
                        currentUser.isPromoter ? TogglePromotion : PromoteUser
                      }
                      w="100%"
                      mt={10}
                      loading={isUserPromotionToggling}
                      color={
                        currentUser.isPromoter
                          ? Colors.OrangePrimary
                          : Colors.GreenPrimary
                      }
                    >
                      Proceed
                    </Button>
                  </Popover.Dropdown>
                </Popover>
                {currentUser.isPromoter && (
                  <Link to={`/dashboard/promoter/${currentUser.id}`}>
                    <Button
                      w={190}
                      variant="outline"
                      color={Colors.BluePrimary}
                      mt={10}
                      ml={5}
                      rightSection={<IconUser size={17} />}
                    >
                      Promoter Profile
                    </Button>
                  </Link>
                )}
                {context?.profile?.role === "admin" && (
                  <Button
                    w={170}
                    variant="outline"
                    color={Colors.GreenPrimary}
                    mt={10}
                    ml={5}
                    onClick={LoginAsUser}
                    loading={isLoggingInAsUser}
                    rightSection={<IconKey size={17} />}
                  >
                    Login As
                  </Button>
                )}
                <Link to={`/dashboard/user/${currentUser.id}`}>
                  <Button
                    w={170}
                    variant="outline"
                    color={Colors.BluePrimary}
                    mt={10}
                    ml={5}
                    rightSection={<IconUser size={17} />}
                  >
                    Full Profile
                  </Button>
                </Link>
              </div>
            </Modal>
          )}
          {users && users.data && (
            <>
              <DatagridDisplay
                rows={users?.data as any}
                isLoading={isLoadingUsers}
                onRowClick={(params) => {
                  OpenUserModal();
                  setCurrentUser(params.row);
                }}
                columns={[
                  {
                    field: "createdAt",
                    width: 150,

                    headerName: "Join Date",
                    renderCell: (
                      params: GridRenderCellParams<UserProfileProps>
                    ) => {
                      return (
                        <div>
                          {dayjs(params.row.createdAt).format("DD MMM YYYY")}
                        </div>
                      );
                    },
                  },
                  {
                    field: "name",
                    flex: 1,

                    headerName: "Name",
                    renderCell: (
                      params: GridRenderCellParams<UserProfileProps>
                    ) => {
                      return <div>{params.row.name}</div>;
                    },
                  },
                  {
                    field: "country",
                    width: 90,
                    headerName: "Country",
                    renderCell: (
                      params: GridRenderCellParams<UserProfileProps>
                    ) => {
                      return (
                        <div>{getCountry(params.row.country)?.flagEmoji}</div>
                      );
                    },
                  },
                  {
                    field: "gender",
                    width: 100,
                    headerName: "Gender",
                    renderCell: (
                      params: GridRenderCellParams<UserProfileProps>
                    ) => {
                      return (
                        <div
                          style={{
                            color: params.row.gender
                              ? params.row.gender === "male"
                                ? Colors.GreenSecondary
                                : Colors.RedPrimary
                              : Colors.BluePrimary,
                            textTransform: "capitalize",
                          }}
                        >
                          {params.row.gender ?? "---"}
                        </div>
                      );
                    },
                  },
                  {
                    field: "phone",
                    width: 140,
                    headerName: "Phone",
                    renderCell: (
                      params: GridRenderCellParams<UserProfileProps>
                    ) => {
                      return <div>{params.row.phone}</div>;
                    },
                  },
                  {
                    field: "email",

                    flex: 1,
                    minWidth: 250,
                    headerName: "Email Address",
                    renderCell: (
                      params: GridRenderCellParams<UserProfileProps>
                    ) => {
                      return (
                        <div>
                          <CopyButton value={params.row.email}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "Copied!" : "Copy to clipboard"}
                              >
                                <span
                                  className="pointer"
                                  onClick={copy}
                                  style={{
                                    color: copied
                                      ? Colors.GreenPrimary
                                      : Colors.BluePrimary,
                                  }}
                                >
                                  {getEllipsisFromString(params.row.email, 50)}
                                </span>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </div>
                      );
                    },
                  },
                  {
                    field: "isVerified",
                    width: 100,

                    headerName: "Is Verified",
                    renderCell: (
                      params: GridRenderCellParams<UserProfileProps>
                    ) => {
                      return (
                        <div>
                          <Badge
                            variant={
                              params.row.isVerified ? "filled" : "outline"
                            }
                            color={
                              params.row.isVerified
                                ? Colors.GreenPrimary
                                : Colors.BluePrimary
                            }
                          >
                            {params.row.isVerified ? "Yes" : "No"}
                          </Badge>
                        </div>
                      );
                    },
                  },
                  {
                    field: "isPromoter",
                    width: 100,

                    headerName: "Promoter",
                    renderCell: (
                      params: GridRenderCellParams<UserProfileProps>
                    ) => {
                      return (
                        <div>
                          <Badge
                            variant={
                              params.row.isPromoter ? "filled" : "outline"
                            }
                            color={
                              params.row.isPromoter
                                ? Colors.GreenPrimary
                                : Colors.BluePrimary
                            }
                          >
                            {params.row.isPromoter ? "Yes" : "No"}
                          </Badge>
                        </div>
                      );
                    },
                  },
                ]}
              />
              <div className="flex-row align-center justify-center width-100">
                <Pagination
                  value={pagination.page}
                  onChange={(value) => {
                    setPagination({ ...pagination, page: value });
                  }}
                  my={40}
                  disabled={users?.data.length === 0 || isLoadingUsers}
                  total={getPaginationTotal(
                    users?.totalRecords,
                    pagination.limit
                  )}
                />
              </div>
            </>
          )}
          {isLoadingUsers && <MegaLoader />}
        </div>

        <Modal
          opened={isNameModalOpened}
          onClose={() => {
            CloseNameModal();
          }}
          centered
          size="xl"
          title="Search Results"
        >
          <DatagridDisplay
            rows={usersByName as any}
            isLoading={isLoadingUsers}
            onRowClick={(params) => {
              OpenUserModal();
              setCurrentUser(params.row);
            }}
            columns={[
              {
                field: "createdAt",
                width: 150,

                headerName: "Join Date",
                renderCell: (
                  params: GridRenderCellParams<UserProfileProps>
                ) => {
                  return (
                    <div>
                      {dayjs(params.row.createdAt).format("DD MMM YYYY")}
                    </div>
                  );
                },
              },
              {
                field: "name",
                flex: 1,

                headerName: "Name",
                renderCell: (
                  params: GridRenderCellParams<UserProfileProps>
                ) => {
                  return <div>{params.row.name}</div>;
                },
              },
              {
                field: "country",
                width: 90,
                headerName: "Country",
                renderCell: (
                  params: GridRenderCellParams<UserProfileProps>
                ) => {
                  return <div>{getCountry(params.row.country)?.flagEmoji}</div>;
                },
              },
              {
                field: "gender",
                width: 100,
                headerName: "Gender",
                renderCell: (
                  params: GridRenderCellParams<UserProfileProps>
                ) => {
                  return (
                    <div
                      style={{
                        color: params.row.gender
                          ? params.row.gender === "male"
                            ? Colors.GreenSecondary
                            : Colors.RedPrimary
                          : Colors.BluePrimary,
                        textTransform: "capitalize",
                      }}
                    >
                      {params.row.gender ?? "---"}
                    </div>
                  );
                },
              },
              {
                field: "phone",
                width: 140,
                headerName: "Phone",
                renderCell: (
                  params: GridRenderCellParams<UserProfileProps>
                ) => {
                  return <div>{params.row.phone}</div>;
                },
              },
              {
                field: "email",

                flex: 1,
                minWidth: 250,
                headerName: "Email Address",
                renderCell: (
                  params: GridRenderCellParams<UserProfileProps>
                ) => {
                  return (
                    <div>
                      <CopyButton value={params.row.email}>
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? "Copied!" : "Copy to clipboard"}
                          >
                            <span
                              className="pointer"
                              onClick={copy}
                              style={{
                                color: copied
                                  ? Colors.GreenPrimary
                                  : Colors.BluePrimary,
                              }}
                            >
                              {getEllipsisFromString(params.row.email, 50)}
                            </span>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </div>
                  );
                },
              },
              {
                field: "isVerified",
                width: 100,

                headerName: "Is Verified",
                renderCell: (
                  params: GridRenderCellParams<UserProfileProps>
                ) => {
                  return (
                    <div>
                      <Badge
                        variant={params.row.isVerified ? "filled" : "outline"}
                        color={
                          params.row.isVerified
                            ? Colors.GreenPrimary
                            : Colors.BluePrimary
                        }
                      >
                        {params.row.isVerified ? "Yes" : "No"}
                      </Badge>
                    </div>
                  );
                },
              },
              {
                field: "isPromoter",
                width: 100,

                headerName: "Promoter",
                renderCell: (
                  params: GridRenderCellParams<UserProfileProps>
                ) => {
                  return (
                    <div>
                      <Badge
                        variant={params.row.isPromoter ? "filled" : "outline"}
                        color={
                          params.row.isPromoter
                            ? Colors.GreenPrimary
                            : Colors.BluePrimary
                        }
                      >
                        {params.row.isPromoter ? "Yes" : "No"}
                      </Badge>
                    </div>
                  );
                },
              },
            ]}
          />
        </Modal>
      </div>
    </Container>
  );
};

export default UsersPage;
